/**
 * Background version-cache refresh — the network half of the update notifier.
 *
 * This runs in a DETACHED child process spawned by a prior foreground run (see
 * `scheduleVersionRefresh` in update-nudge.ts), never in the foreground. It hits npm / GitHub, writes the
 * result to the on-disk cache, and exits. It NEVER prints — the foreground run shows the nudge from
 * the cache this populates (the "show on next run" pattern: a refresh feeds the *next* invocation,
 * so the current command is never slowed). All failures are swallowed: a stale nudge is fine, a
 * crashed background refresher is not.
 */

import { exec } from 'node:child_process';
import { CLI_CACHE_FILE, SKILL_CACHE_FILE, ADMIN_SKILL_CACHE_FILE, writeVersionCache } from './version-cache.js';
import { DEFAULT_SKILL_RAW_URL, ADMIN_SKILL_NAME, SKILL_FILENAME, parseFrontmatterVersion } from './skill-version.js';

// Generous because nothing waits on us: bounds the orphaned process, not user latency.
const REFRESH_TIMEOUT_MS = 10_000;

function refreshCliCache(): Promise<void> {
  return new Promise((resolve) => {
    // Shell `exec` (fixed command string, no interpolation) resolves npm's `.cmd` shim on Windows
    // without the args-array + shell:true combination that triggers Node's DEP0190 warning.
    exec('npm view @wayai/cli version', { timeout: REFRESH_TIMEOUT_MS }, (err, stdout) => {
      if (!err) {
        const latest = stdout.trim();
        if (latest) {
          try { writeVersionCache(CLI_CACHE_FILE, { lastCheck: Date.now(), latest }); } catch { /* ignore */ }
        }
      }
      resolve();
    });
  });
}

async function refreshSkillCache(): Promise<void> {
  const url = process.env.WAYAI_SKILL_RAW_URL || DEFAULT_SKILL_RAW_URL;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (res.ok) {
      const latest = parseFrontmatterVersion(await res.text());
      if (latest) {
        try { writeVersionCache(SKILL_CACHE_FILE, { lastCheck: Date.now(), latest }); } catch { /* ignore */ }
      }
    }
  } catch {
    /* network failure: leave the cache as-is */
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Refresh the private wayai-admin skill's latest version. Unlike the CLI/public-skill refreshes,
 * the source is the platform-admin-gated endpoint, so this needs auth. Non-interactive by design:
 * if there's no stored token (`getAccessToken` throws) or the caller isn't a platform admin (the
 * request 403s), we swallow it and leave the cache empty — so a non-admin never gets an admin nudge.
 */
async function refreshAdminSkillCache(): Promise<void> {
  // Bound the orphaned process like the sibling refreshers. The auth + API calls use Node's global
  // `fetch`, which has NO short default timeout, so race them against the same REFRESH_TIMEOUT_MS
  // deadline. When the timer wins, `refreshVersionCaches` resolves and index.ts's `process.exit(0)`
  // reaps the still-pending fetch — a stalled connection can't keep the detached process alive past
  // the deadline. The `.catch` keeps a post-deadline rejection from surfacing as an unhandled
  // rejection; every failure (not logged in / 403 / network) just leaves the cache empty.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<void>((resolve) => { timer = setTimeout(resolve, REFRESH_TIMEOUT_MS); });
  await Promise.race([deadline, fetchAndCacheAdminSkillVersion().catch(() => {})]);
  if (timer) clearTimeout(timer);
}

async function fetchAndCacheAdminSkillVersion(): Promise<void> {
  // Lazy-import the auth/API stack — index.ts loads this module eagerly on every CLI run, but
  // these deps (keyring, http client) are only needed in the detached refresher. Dynamic import
  // keeps the foreground hot path (and `wayai --version`) from paying for them.
  const [{ readConfig }, { getAccessToken }, { ApiClient }] = await Promise.all([
    import('./config.js'),
    import('./auth.js'),
    import('./api-client.js'),
  ]);
  const config = readConfig();
  if (!config) return; // not logged in → can't reach the gated endpoint
  const accessToken = await getAccessToken(config);
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  const res = await client.adminSkillInstall(ADMIN_SKILL_NAME);
  const latest = parseFrontmatterVersion(res.files[SKILL_FILENAME] ?? '');
  if (latest) {
    try { writeVersionCache(ADMIN_SKILL_CACHE_FILE, { lastCheck: Date.now(), latest }); } catch { /* ignore */ }
  }
}

export async function refreshVersionCaches(opts: {
  cli: boolean;
  skill: boolean;
  adminSkill: boolean;
}): Promise<void> {
  await Promise.all([
    opts.cli ? refreshCliCache() : Promise.resolve(),
    opts.skill ? refreshSkillCache() : Promise.resolve(),
    opts.adminSkill ? refreshAdminSkillCache() : Promise.resolve(),
  ]);
}
